import React, { useEffect, useMemo, useState } from "react";
import { Player, SavedTournament } from "../types.ts";
import { getAllPlayers, getAllResults } from "../utils/firebase-utils.js";
import {
  buildStandings,
  getChampionshipSummary,
  getParticipantCount,
  getSeasonTitle,
  sortTournamentsByDateDesc,
} from "../utils/championship.ts";
import { formatTime } from "../utils/tournament-calc.ts";
import PublicHeader from "../components/public/PublicHeader.tsx";
import ChampionshipBoard from "../components/public/ChampionshipBoard.tsx";
import TournamentHistory from "../components/public/TournamentHistory.tsx";
import TournamentResultTable from "../components/TournamentResultTable.tsx";
import ChessPiece, {
  ChessPieceName,
} from "../components/public/ChessPiece.tsx";

import "../components/public/ChessTheme.css";

/** Karta s jedním číslem. Popisek větnou formou, hodnota polotučně. */
const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  piece: ChessPieceName;
}> = ({ label, value, hint, piece }) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <ChessPiece
      name={piece}
      className="pointer-events-none absolute -right-3 -bottom-3 h-20 w-20 text-slate-900/5"
    />
    <div className="relative">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-1 text-3xl font-semibold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  </div>
);

const PODIUM_STYLES = [
  {
    badge: "bg-amber-400 text-amber-950",
    ring: "border-amber-200 bg-amber-50",
    label: "1. místo",
  },
  {
    badge: "bg-slate-300 text-slate-800",
    ring: "border-slate-200 bg-slate-50",
    label: "2. místo",
  },
  {
    badge: "bg-orange-300 text-orange-950",
    ring: "border-orange-200 bg-orange-50",
    label: "3. místo",
  },
];

const PublicSite: React.FC = () => {
  const [tournaments, setTournaments] = useState<SavedTournament[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const load = async () => {
      try {
        const [results, players] = await Promise.all([
          getAllResults(),
          getAllPlayers(),
        ]);
        setTournaments(sortTournamentsByDateDesc(results as SavedTournament[]));
        setAllPlayers(players as Player[]);
      } catch (err) {
        console.error("Chyba při načítání veřejných dat:", err);
        setError(
          "Výsledky se nepodařilo načíst. Zkuste stránku prosím načíst znovu.",
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const standings = useMemo(
    () => buildStandings(allPlayers, tournaments),
    [allPlayers, tournaments],
  );

  const activeStandings = useMemo(
    () => standings.filter((row) => row.attended > 0),
    [standings],
  );

  const summary = useMemo(
    () => getChampionshipSummary(tournaments, standings),
    [tournaments, standings],
  );

  const latest = tournaments[0] || null;

  // Ročník se řídí posledním odehraným turnajem
  const title = useMemo(() => getSeasonTitle(tournaments), [tournaments]);

  /** Stupně vítězů posledního turnaje. */
  const podium = useMemo(() => {
    if (!latest) return [];
    const count = getParticipantCount(latest);

    return [1, 2, 3]
      .map((rank) => {
        for (let i = 0; i < count; i++) {
          if (Number(latest.rankings?.[i]) === rank) {
            return {
              rank,
              name:
                latest.playerNames?.[i] ||
                allPlayers.find((p) => p.id === latest.player?.[i])
                  ?.player_name ||
                "—",
              points: latest.points?.[i],
              time: latest.times?.[i] ?? null,
              newTime: latest.newTimes?.[i] ?? null,
            };
          }
        }
        return null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [latest, allPlayers]);

  const handleTournamentUpdated = (updated: SavedTournament) => {
    setTournaments((current) =>
      current.map((t) => (t.id === updated.id ? updated : t)),
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <PublicHeader title={title} />

      {/* Hero */}

      <main className="mx-auto max-w-6xl px-4 py-10 space-y-12">
        {loading && (
          <p className="text-center text-slate-500 py-12">Načítám výsledky…</p>
        )}

        {error && !loading && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
            <p className="font-medium text-rose-800">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Souhrn */}
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                piece="rook"
                label="Odehraných turnajů"
                value={summary.tournamentCount}
              />
              <StatTile
                piece="pawn"
                label="Hráčů v sezoně"
                value={summary.playerCount}
              />
              <StatTile
                piece="bishop"
                label="Odehraných partií"
                value={summary.gamesPlayed}
                hint="Každý s každým"
              />
              <StatTile
                piece="queen"
                label="Poslední turnaj"
                value={latest ? latest.date : "—"}
                hint={
                  latest
                    ? `${getParticipantCount(latest)} hráčů`
                    : "zatím žádný"
                }
              />
            </section>

            {/* Průběžné pořadí – samostatná šachová sekce */}
            <ChampionshipBoard
              standings={activeStandings}
              tournaments={tournaments}
            />

            {/* Poslední turnaj */}
            {latest && (
              <section>
                <div className="flex items-baseline justify-between gap-3 mb-5">
                  <h2 className="flex items-center gap-2 text-2xl font-bold">
                    <ChessPiece
                      name="bishop"
                      className="h-6 w-6 text-indigo-500"
                    />
                    Poslední turnaj
                  </h2>
                  <span className="text-slate-500 tabular-nums">
                    {latest.date}
                  </span>
                </div>

                {(latest.description || latest.comment) && (
                  <div className="mb-5 rounded-2xl border border-indigo-100 bg-indigo-50 p-5">
                    {latest.description && (
                      <p className="font-medium text-slate-800">
                        {latest.description}
                      </p>
                    )}
                    {latest.comment && (
                      <p className="mt-1 text-sm text-slate-600 whitespace-pre-line">
                        {latest.comment}
                      </p>
                    )}
                  </div>
                )}

                {podium.length > 0 && (
                  <div className="mb-6 grid gap-4 sm:grid-cols-3">
                    {podium.map((entry, index) => {
                      const style = PODIUM_STYLES[index] || PODIUM_STYLES[2];
                      return (
                        <div
                          key={entry.rank}
                          className={`rounded-2xl border p-5 ${style.ring}`}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold ${style.badge}`}
                            >
                              {entry.rank}
                            </span>
                            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                              {style.label}
                            </span>
                          </div>
                          <p className="mt-3 text-xl font-semibold text-slate-900">
                            {entry.name}
                          </p>
                          <p className="mt-1 text-sm text-slate-600 tabular-nums">
                            {entry.points !== undefined
                              ? `${entry.points} b.`
                              : ""}
                            {entry.time !== null
                              ? ` · hrál na ${formatTime(entry.time)}`
                              : ""}
                          </p>
                          {entry.newTime !== null && (
                            <p className="text-xs text-slate-500 tabular-nums">
                              nový čas {formatTime(entry.newTime)}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <TournamentResultTable
                    tournament={latest}
                    allPlayers={allPlayers}
                  />
                </div>
              </section>
            )}

            {/* Historie */}
            <section>
              <h2 className="mb-5 flex items-center gap-2 text-2xl font-bold">
                <ChessPiece name="rook" className="h-6 w-6 text-indigo-500" />
                Historie turnajů
              </h2>
              <TournamentHistory
                tournaments={tournaments}
                allPlayers={allPlayers}
                onTournamentUpdated={handleTournamentUpdated}
              />
            </section>
          </>
        )}
      </main>

      <footer className="pc-board-light border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-6 text-sm text-slate-500">
          <span className="flex gap-1 text-slate-300" aria-hidden="true">
            <ChessPiece name="pawn" className="h-5 w-5" />
            <ChessPiece name="knight" className="h-5 w-5" />
            <ChessPiece name="bishop" className="h-5 w-5" />
            <ChessPiece name="rook" className="h-5 w-5" />
            <ChessPiece name="queen" className="h-5 w-5" />
            <ChessPiece name="king" className="h-5 w-5" />
          </span>
          {title} · výsledky šachových turnajů
        </div>
      </footer>
    </div>
  );
};

export default PublicSite;
